import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

import {
  APP_HEADER_HEIGHT,
  APP_HEADER_HEIGHT_COMPACT,
  useMuiSwitchableTheme,
} from '@farmshare/ui-components';

import { ProducerNavModule } from '../producer-nav-config';

const ICON_RAIL_WIDTH = 64;
const SEC_NAV_WIDTH = 226;
const HOVER_DEBOUNCE_MS = 150;

function getStoredBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  const stored = localStorage.getItem(key);
  if (stored === null) return fallback;
  return stored === 'true';
}

export function useProducerNav(modules: ProducerNavModule[]) {
  const { pathname } = useLocation();
  // A phone on its side is wider than 899px but is still a phone.
  const isNarrow = useMediaQuery('(max-width: 899px)');
  // The theme's own reading, so the bar and `--app-header-height` flip on
  // the same render.
  const { isPhoneLandscape } = useMuiSwitchableTheme();
  const isMobile = isNarrow || isPhoneLandscape;
  // The theme's `marginTop` is this same choice, but only once the nav mode
  // is 'top' — which the shell sets from `isMobile` here, a render later.
  const MOBILE_TOP_BAR_HEIGHT = isPhoneLandscape
    ? APP_HEADER_HEIGHT_COMPACT
    : APP_HEADER_HEIGHT;

  // Persisted state
  const [isPinned, setIsPinned] = useState(() =>
    getStoredBoolean('producerNavPinned', false),
  );

  // Transient state
  const [hoveredModule, setHoveredModuleState] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Hover debounce
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const setHoveredModule = useCallback(
    (moduleId: string | null) => {
      clearHoverTimer();
      if (moduleId) {
        setHoveredModuleState(moduleId);
      } else {
        hoverTimerRef.current = setTimeout(() => {
          setHoveredModuleState(null);
        }, HOVER_DEBOUNCE_MS);
      }
    },
    [clearHoverTimer],
  );

  // Cleanup timer on unmount
  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  // Active module detection
  const activeModule = useMemo(() => {
    // Buyer funnel pages (cart / claim / animal detail) aren't nav targets
    // themselves, so they'd otherwise leave the rail dark. Keep the
    // "Available Splits" module lit while the buyer moves through the funnel.
    const BUYER_FUNNEL_PREFIXES = ['/buyer/cart', '/buyer/animal'];
    if (BUYER_FUNNEL_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
      const availableModule = modules.find((m) => m.id === 'available');
      if (availableModule) return availableModule.id;
    }

    for (const mod of modules) {
      // An href may carry a query (a dashboard tab); match on its path.
      if (mod.href && pathname.startsWith(mod.href.split('?')[0]))
        return mod.id;
      if (mod.sections) {
        for (const section of mod.sections) {
          for (const item of section.items) {
            if (pathname.startsWith(item.href)) return mod.id;
          }
        }
      }
    }
    return null;
  }, [pathname, modules]);

  // The module to show in secnav: hovered takes priority, then active if pinned
  const visibleModule = hoveredModule ?? (isPinned ? activeModule : null);
  // Only show sec nav if the visible module actually has sections
  const visibleModuleData = visibleModule
    ? modules.find((m) => m.id === visibleModule)
    : undefined;
  const isSecNavVisible =
    visibleModule !== null &&
    !!visibleModuleData?.sections &&
    visibleModuleData.sections.length > 0;

  // Toggle pin
  const togglePin = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      localStorage.setItem('producerNavPinned', String(next));
      return next;
    });
  }, []);

  // Mobile drawer
  const toggleMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen((prev) => !prev);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, []);

  const contentMarginTop = useMemo(
    () => (isMobile ? MOBILE_TOP_BAR_HEIGHT : 0),
    [isMobile, MOBILE_TOP_BAR_HEIGHT],
  );

  return {
    isMobile,
    isPinned,
    togglePin,
    hoveredModule,
    setHoveredModule,
    activeModule,
    visibleModule,
    isSecNavVisible,
    isMobileDrawerOpen,
    toggleMobileDrawer,
    closeMobileDrawer,

    contentMarginTop,
    ICON_RAIL_WIDTH,
    SEC_NAV_WIDTH,
    MOBILE_TOP_BAR_HEIGHT,
  };
}
